import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Sparkles, User } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { TID } from "@/constants/testIds";

export default function Dashboard() {
  const { user } = useAuth();
  const [favs, setFavs] = useState([]);

  useEffect(() => {
    api.get("/favorites").then((r) => setFavs(r.data)).catch(() => setFavs([]));
  }, []);

  if (!user) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6 text-center">
        <div>
          <h2 className="font-display text-4xl">Sign in to see your Hey GC.</h2>
          <div className="mt-6 flex gap-3 justify-center">
            <Link to="/login" className="btn-pill btn-primary">Sign in</Link>
            <Link to="/register" className="btn-pill btn-outline">Create account</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-16 pb-24">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <span className="editorial-divider" />
        <h1 className="font-display text-5xl md:text-6xl mt-4 tracking-tight">
          Hey <span className="italic">{user.name.split(" ")[0]}</span>.
        </h1>
        <p className="text-[color:var(--forest-2)] mt-4">Welcome to your coast, curated.</p>
      </motion.div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/trip-planner" className="bg-white rounded-2xl border border-[color:var(--forest-line)] p-6 card-lift block">
          <Sparkles size={18} className="text-[color:var(--terracotta)]" />
          <div className="mt-4 font-display text-2xl">Plan a trip</div>
          <div className="text-sm text-[color:var(--forest-2)] mt-1">AI-crafted day-by-day itinerary.</div>
        </Link>
        <Link to="/concierge" className="bg-white rounded-2xl border border-[color:var(--forest-line)] p-6 card-lift block">
          <User size={18} className="text-[color:var(--tropical-sea)]" />
          <div className="mt-4 font-display text-2xl">Chat concierge</div>
          <div className="text-sm text-[color:var(--forest-2)] mt-1">Ask the coast anything.</div>
        </Link>
        <Link to="/events" className="bg-white rounded-2xl border border-[color:var(--forest-line)] p-6 card-lift block">
          <Heart size={18} className="text-[color:var(--terracotta)]" />
          <div className="mt-4 font-display text-2xl">Discover more</div>
          <div className="text-sm text-[color:var(--forest-2)] mt-1">Fresh events every week.</div>
        </Link>
      </div>

      <div className="mt-16" data-testid={TID.dashSaved}>
        <h2 className="font-display text-3xl">Your favourites</h2>
        {favs.length === 0 ? (
          <div className="mt-6 bg-white rounded-2xl border border-[color:var(--forest-line)] p-10 text-center text-[color:var(--forest-2)]">
            No saves yet — tap the heart on any event, experience or place.
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {favs.map((f) => {
              const item = f.item;
              const to =
                f.type === "event" ? `/events/${item.slug}` :
                f.type === "experience" ? `/experiences/${item.slug}` :
                f.type === "guide" ? `/guides/${item.slug}` :
                `/directory/${item.slug}`;
              return (
                <Link key={f.favorite_id} to={to} className="bg-white rounded-2xl border border-[color:var(--forest-line)] overflow-hidden card-lift">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={item.image || item.cover_image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <div className="text-[0.7rem] uppercase tracking-[0.2em] text-[color:var(--forest-2)]">{f.type}</div>
                    <div className="font-display text-lg mt-1">{item.title || item.name}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
